import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    ResizableHandleCursorConstantType,
    ResizableHandleColorsConstantType,
    ResizableHandleDynamicStyleType,
    ResizableHandleHitConstantType,
    ResizableHandleColors
} from "./type";
import type {
    Mutable
} from "../../../../types";
import type {
    ResizableOrientation
} from "../../type";
import {
    getElevationStyle
} from "../../../../helpers/theme/elevation";
import {
    getFocusRingStyle
} from "../../../../helpers/theme/state";
import {
    getMotionTransition
} from "../../../../helpers/motion";
import {
    getOpaqueColor,
    webStyle
} from "../../../../utils";

export const RESIZABLE_HANDLE_SIZE_KEYS: Record<ResizableOrientation, "width" | "height"> = {
    horizontal: "width",
    vertical: "height"
};

export const RESIZABLE_HANDLE_MARGIN_KEYS: Record<ResizableOrientation, Record<"start" | "end", "marginStart" | "marginEnd" | "marginTop" | "marginBottom">> = {
    horizontal: {
        start: "marginStart",
        end: "marginEnd"
    },
    vertical: {
        end: "marginBottom",
        start: "marginTop"
    }
};

export const RESIZABLE_HANDLE_IDLE_CURSORS: Record<ResizableOrientation, string> = {
    horizontal: "col-resize",
    vertical: "row-resize"
};

export const RESIZABLE_HANDLE_HALF = 2;

export const getHandleHit = ({
    touchTargets,
    pointerType
}: ResizableHandleHitConstantType): number => {
    return pointerType === "coarse" ? touchTargets.fine : touchTargets.handle;
};

export const getHandleCursor = ({
    orientation,
    isDisabled,
    direction,
    limit
}: ResizableHandleCursorConstantType): string => {
    if(isDisabled) {
        return "default";
    }

    if(!limit) {
        return RESIZABLE_HANDLE_IDLE_CURSORS[orientation];
    }

    if(orientation === "vertical") {
        return limit === "min" ? "s-resize" : "n-resize";
    }

    const isRTL = direction === "rtl";

    if(limit === "min") {
        return isRTL ? "w-resize" : "e-resize";
    }

    return isRTL ? "e-resize" : "w-resize";
};

export const getHandleColors = ({
    isDisabled,
    isDragging,
    isHover,
    colors
}: ResizableHandleColorsConstantType): ResizableHandleColors => {
    if(isDisabled) {
        return {
            gripBackgroundColor: getOpaqueColor({
                color: colors.system.state.overlay.disabled.neutral,
                surface: colors.system.default.background
            }),
            lineColor: colors.system.state.border.disabled.neutral,
            iconColorKey: "disabled"
        };
    }

    const gripBackgroundColor = colors.content.container.raised;

    if(isDragging) {
        return {
            lineColor: colors.content.border.emphasized,
            iconColorKey: "emphasized",
            gripBackgroundColor
        };
    }

    if(isHover) {
        return {
            lineColor: colors.content.border.hover,
            iconColorKey: "default",
            gripBackgroundColor
        };
    }

    return {
        lineColor: colors.content.border.subtle,
        iconColorKey: "low",
        gripBackgroundColor
    };
};

export const RESIZABLE_HANDLE_GRIP_DOTS: Record<ResizableOrientation, Array<Array<number>>> = {
    horizontal: [
        [
            0,
            1
        ],
        [
            2,
            3
        ],
        [
            4,
            5
        ]
    ],
    vertical: [
        [
            0,
            1,
            2
        ],
        [
            3,
            4,
            5
        ]
    ]
};

export const getHandleGripMeasures = ({
    orientation,
    iconSizes,
    borders,
    spaces
}: Pick<ResizableHandleDynamicStyleType, "orientation" | "iconSizes" | "borders" | "spaces">) => {
    const short = iconSizes.small;

    const long = iconSizes.large + borders.indicator;

    return {
        height: orientation === "horizontal" ? long : short,
        width: orientation === "horizontal" ? short : long,
        dotSize: borders.halo,
        gap: spaces.spacingXs
    };
};

const stylesheet = StyleSheet.create({
    pan: {
        ...StyleSheet.absoluteFill,
        ...webStyle({
            touchAction: "none"
        })
    },
    container: {
        justifyContent: "center",
        alignItems: "center",
        flexShrink: 0,
        ...webStyle({
            outlineStyle: "solid",
            touchAction: "none",
            userSelect: "none",
            outlineWidth: 0
        })
    },
    grip: {
        justifyContent: "center",
        pointerEvents: "none",
        alignItems: "center",
        borderStyle: "solid",
        position: "absolute"
    },
    lineHorizontal: {
        pointerEvents: "none",
        height: "100%"
    },
    lineVertical: {
        pointerEvents: "none",
        width: "100%"
    },
    gripRow: {
        flexDirection: "row"
    }
});

export const useStyles = ({
    isReduceMotionEnabled,
    isFocusVisible,
    currentColors,
    isGripVisible,
    shapeConfigs,
    orientation,
    shadowAlpha,
    elevations,
    isDisabled,
    isDragging,
    direction,
    durations,
    iconSizes,
    radiuses,
    borders,
    isHover,
    colors,
    spaces,
    limit,
    hit
}: ResizableHandleDynamicStyleType) => {
    const gripMeasures = getHandleGripMeasures({
        orientation,
        iconSizes,
        borders,
        spaces
    });

    const lineThickness = isDragging || isHover ? borders.focus : borders.separator;

    const marginKeys = RESIZABLE_HANDLE_MARGIN_KEYS[orientation];

    const margin = (borders.separator - hit) / RESIZABLE_HANDLE_HALF;

    const styles = {
        gripDot: {
            backgroundColor: colors.content.icon[currentColors.iconColorKey],
            height: gripMeasures.dotSize,
            borderRadius: radiuses.full,
            width: gripMeasures.dotSize
        } as Mutable<ViewStyle>,
        grip: {
            backgroundColor: currentColors.gripBackgroundColor,
            borderColor: currentColors.lineColor,
            borderRadius: radiuses.full,
            height: gripMeasures.height,
            width: gripMeasures.width,
            borderWidth: borders.line,
            gap: gripMeasures.gap
        } as Mutable<ViewStyle>,
        line: {
            backgroundColor: currentColors.lineColor
        } as Mutable<ViewStyle>,
        container: {
            zIndex: shapeConfigs.layers.raised,
            ...webStyle({
                cursor: getHandleCursor({
                    orientation,
                    isDisabled,
                    direction,
                    limit
                })
            })
        } as Mutable<ViewStyle>,
        gripRow: {
            gap: gripMeasures.gap
        } as Mutable<ViewStyle>
    };

    Object.assign(styles.container, {
        [RESIZABLE_HANDLE_SIZE_KEYS[orientation]]: hit,
        [marginKeys.start]: margin,
        [marginKeys.end]: margin
    });

    Object.assign(styles.line, {
        [RESIZABLE_HANDLE_SIZE_KEYS[orientation]]: lineThickness
    });

    const transition = getMotionTransition({
        properties: [
            "background-color",
            "border-color"
        ],
        isReduceMotionEnabled,
        intent: "hover",
        durations
    });

    if(isDragging) {
        transition.transitionDuration = "0ms";
    }

    Object.assign(styles.grip, webStyle(transition));

    Object.assign(styles.line, webStyle({
        ...transition,
        transitionProperty: "background-color"
    }));

    if(!isDisabled) {
        Object.assign(styles.grip, getElevationStyle({
            shadowColor: colors.system.shadow,
            level: "level1",
            shadowAlpha,
            elevations
        }));
    }

    Object.assign(isGripVisible ? styles.grip : styles.line, getFocusRingStyle({
        isFocusVisible,
        borders,
        colors
    }));

    return styles;
};
export default stylesheet;
